// Single research SKU card. Renders the compound's identity, batch
// metadata, COA link, and a variant toggle (subscription / one-time)
// with an Add-to-tray action. Strictly compliance-clean: no medical
// claims, no human imagery copy.
import { useMemo, useState } from "react";
import Button from "@/components/Button";
import { C } from "@/lib/brand";
import { fmtMoney } from "@/lib/format";
import { RESEARCH_NAVY, RESEARCH_NAVY_DEEP } from "@/components/research/ResearchHero";

const AVAILABILITY_LABEL = {
  in_stock: "IN STOCK",
  backorder: "BACKORDER",
  out_of_stock: "OUT OF STOCK",
  coming_soon: "COMING SOON",
};

export default function ResearchItemCard({ item, isInTray, onAdd, onRemove, onLearnMore, onNotify }) {
  // Default variant: subscription if available, otherwise one_time.
  const hasSub = item.monthlyPrice != null;
  const hasOne = item.oneTimePrice != null;
  const initialVariant = hasSub ? "subscription" : "one_time";
  const [variant, setVariant] = useState(initialVariant);

  const priceCents = variant === "subscription" ? item.monthlyPrice : item.oneTimePrice;
  const priceLabel = useMemo(() => {
    if (priceCents == null) return "—";
    return fmtMoney(priceCents / 100, (item.currency || "usd").toUpperCase());
  }, [priceCents, item.currency]);

  const available = item.availability === "in_stock" || item.availability === "backorder";
  const availabilityLabel = AVAILABILITY_LABEL[item.availability] || "UNAVAILABLE";

  const cardKey = `${item.sku}-${variant}`;

  return (
    <article
      data-testid={`research-item-card-${item.sku}`}
      data-variant={variant}
      style={{
        background: C.pure,
        border: `1px solid ${C.warmGray4}`,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Header strip — mono batch ID + availability */}
      <div
        style={{
          padding: "12px 18px",
          background: "rgba(31,51,73,0.04)",
          borderBottom: `1px solid ${C.warmGray4}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
          fontSize: 10,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: RESEARCH_NAVY,
        }}
      >
        <span data-testid={`research-item-batch-${item.sku}`}>
          {item.batchId || item.sku}
        </span>
        <span
          data-testid={`research-item-availability-${item.sku}`}
          style={{
            color: available ? RESEARCH_NAVY : C.peachDark,
            fontWeight: 800,
          }}
        >
          {availabilityLabel}
        </span>
      </div>

      {/* Body */}
      <div style={{ padding: "24px 18px 18px", flex: 1, display: "flex", flexDirection: "column" }}>
        <h3
          data-testid={`research-item-name-${item.sku}`}
          style={{
            margin: 0,
            fontSize: 26,
            fontWeight: 800,
            letterSpacing: "-0.02em",
            color: RESEARCH_NAVY_DEEP,
            fontFamily: '"DM Sans", sans-serif',
            textTransform: "uppercase",
            lineHeight: 1.05,
          }}
        >
          {item.name}
        </h3>
        <div
          data-testid={`research-item-ingredient-${item.sku}`}
          style={{
            marginTop: 6,
            fontSize: 13,
            color: C.warmGray,
            fontStyle: "italic",
            fontFamily: '"Instrument Serif", Georgia, serif',
            letterSpacing: "0.01em",
          }}
        >
          {item.ingredient || item.name}
        </div>
        {item.form && (
          <div
            data-testid={`research-item-form-${item.sku}`}
            style={{
              marginTop: 10,
              fontSize: 12,
              letterSpacing: "0.05em",
              color: C.warmGray,
            }}
          >
            {item.form}
          </div>
        )}

        {/* Variant toggle (only show segments that exist) */}
        {hasSub && hasOne && (
          <div
            data-testid={`research-item-variant-toggle-${item.sku}`}
            style={{
              marginTop: 22,
              display: "inline-flex",
              alignSelf: "flex-start",
              border: `1px solid ${C.warmGray3}`,
            }}
          >
            <VariantBtn
              active={variant === "subscription"}
              onClick={() => setVariant("subscription")}
              testId={`research-item-variant-sub-${item.sku}`}
            >
              SUBSCRIBE
            </VariantBtn>
            <VariantBtn
              active={variant === "one_time"}
              onClick={() => setVariant("one_time")}
              testId={`research-item-variant-one-${item.sku}`}
            >
              ONE-TIME
            </VariantBtn>
          </div>
        )}

        {/* Price + COA */}
        <div style={{ marginTop: 24, display: "flex", alignItems: "baseline", gap: 10 }}>
          <span
            data-testid={`research-item-price-${item.sku}`}
            style={{
              fontSize: 28,
              fontWeight: 800,
              color: RESEARCH_NAVY_DEEP,
              fontFamily: '"DM Sans", sans-serif',
              letterSpacing: "-0.02em",
            }}
          >
            {priceLabel}
          </span>
          <span
            style={{
              fontSize: 11,
              letterSpacing: "0.18em",
              color: C.warmGray,
              textTransform: "uppercase",
            }}
          >
            {variant === "subscription" ? "/ month" : "/ vial"}
          </span>
        </div>

        {item.coaUrl && (
          <a
            data-testid={`research-item-coa-${item.sku}`}
            href={item.coaUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              marginTop: 14,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.18em",
              color: RESEARCH_NAVY,
              textTransform: "uppercase",
              textDecoration: "none",
              borderBottom: `1.5px solid ${RESEARCH_NAVY}`,
              alignSelf: "flex-start",
              paddingBottom: 2,
            }}
          >
            View COA →
          </a>
        )}

        {/* Footnote: "Also in: Weight Management" if program-bound */}
        {Array.isArray(item.programSlugs) && item.programSlugs.length > 0 && (
          <div
            data-testid={`research-item-program-footnote-${item.sku}`}
            style={{
              marginTop: 18,
              fontSize: 11,
              letterSpacing: "0.05em",
              color: C.warmGray,
            }}
          >
            Also part of {item.programSlugs.map(slugTitle).join(", ")} program{item.programSlugs.length > 1 ? "s" : ""}.
          </div>
        )}

        {/* LEARN MORE — opens the detail drawer. Shown whenever the SKU
            carries any of the doc fields (description / benefits /
            mechanism / dosing / references / attachments / videoUrl /
            contraindications). Progressive: hidden entirely on SKUs
            with no docs to avoid a dead-end click. */}
        {hasDocs(item) && (
          <button
            type="button"
            onClick={() => onLearnMore?.(item)}
            data-testid={`research-item-learn-more-${item.sku}`}
            style={{
              marginTop: 14,
              alignSelf: "flex-start",
              background: "transparent",
              border: 0,
              padding: "4px 0",
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.18em",
              color: RESEARCH_NAVY,
              textTransform: "uppercase",
              cursor: "pointer",
              borderBottom: `1.5px solid ${RESEARCH_NAVY}`,
              fontFamily: "inherit",
            }}
          >
            LEARN MORE →
          </button>
        )}

        <div style={{ flex: 1 }} />

        {/* Action */}
        <div style={{ marginTop: 26 }}>
          {isInTray ? (
            <Button
              testId={`research-item-remove-${item.sku}`}
              variant="ghost"
              block
              onClick={() => onRemove(item, variant)}
            >
              ✓ Added · Remove
            </Button>
          ) : available ? (
            <Button
              testId={`research-item-add-${item.sku}`}
              variant="primary"
              block
              disabled={priceCents == null}
              onClick={() => onAdd(item, variant)}
            >
              Add to plan →
            </Button>
          ) : (
            <Button
              testId={`research-item-notify-${item.sku}`}
              variant="primary"
              block
              onClick={() => onNotify?.(item)}
            >
              NOTIFY ME WHEN AVAILABLE →
            </Button>
          )}
        </div>
      </div>

      {/* Inner key for React reconciliation across variant changes */}
      <span hidden data-card-key={cardKey} />
    </article>
  );
}

function VariantBtn({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      data-active={active ? "true" : "false"}
      style={{
        padding: "9px 14px",
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        background: active ? RESEARCH_NAVY_DEEP : "transparent",
        color: active ? C.cream : RESEARCH_NAVY,
        cursor: "pointer",
        border: "none",
        borderRight: `1px solid ${C.warmGray3}`,
      }}
    >
      {children}
    </button>
  );
}

function slugTitle(slug) {
  return (slug || "")
    .split("-")
    .filter(Boolean)
    .map((s) => s[0].toUpperCase() + s.slice(1))
    .join(" ");
}

// True when a SKU has any documentation attached — used to gate the
// "LEARN MORE →" affordance on the card. We keep the drawer trigger
// hidden on SKUs with nothing to show so members don't tap into an
// empty panel.
function hasDocs(item) {
  return !!(
    item.description ||
    item.mechanism ||
    item.dosing ||
    item.contraindications ||
    item.videoUrl ||
    (Array.isArray(item.benefits) && item.benefits.length > 0) ||
    (Array.isArray(item.references) && item.references.length > 0) ||
    (Array.isArray(item.attachments) && item.attachments.length > 0)
  );
}
